import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Inbox, ShieldAlert, Copy, CheckCircle2, XCircle, PauseCircle, RotateCcw,
  Plus, ClipboardPaste, X, Loader2, Building2, KeyRound, AlertTriangle,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useReference } from "@/context/ReferenceContext";
import { PageHeader } from "@/components/CrudModule";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const STATUSES = ["Available for Review", "Hold for Review", "Returned for Missing Information", "Accepted", "Rejected"];
const STATUS_CLS = {
  "Available for Review": "bg-[#315A7D]/10 text-[#2c5170] ring-[#315A7D]/25",
  "Accepted": "bg-emerald-50 text-emerald-700 ring-emerald-200",
  "Rejected": "bg-red-50 text-red-700 ring-red-200",
  "Hold for Review": "bg-[#B28A3D]/12 text-[#7a5d27] ring-[#B28A3D]/30",
  "Returned for Missing Information": "bg-amber-50 text-amber-700 ring-amber-200",
};

export default function LeadReview() {
  const { opts } = useReference();
  const [status, setStatus] = useState("Available for Review");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(null);
  const [returning, setReturning] = useState(null);
  const [showPaste, setShowPaste] = useState(false);
  const [showVendors, setShowVendors] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/intake/submissions", { params: { status } }).then(({ data }) => setRows(data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, [status]);

  const decide = async (sub, action, extra = {}) => {
    try {
      await api.post(`/intake/submissions/${sub.id}/decision`, { action, ...extra });
      toast.success(`Submission ${action === "accept" ? "accepted into pipeline" : action}`);
      setAccepting(null); setReturning(null); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const counts = STATUSES.reduce((a, s) => ({ ...a, [s]: rows.length }), {});

  return (    <div>
      <PageHeader title="Lead Review Queue" subtitle="Vendor submissions land here. A submission is NOT an accepted lead — accept, reject, hold or return each one. Only accepted leads enter the pipeline." />

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {STATUSES.map((s) => (
          <button key={s} onClick={() => setStatus(s)} data-testid={`filter-${s.replace(/[^a-z]+/gi, "-")}`}
            className={`rounded-full px-3 py-1.5 text-xs font-medium ring-1 ring-inset transition-colors ${status === s ? STATUS_CLS[s] : "bg-card text-muted-foreground ring-border hover:bg-secondary"}`}>
            {s}
          </button>
        ))}
        <div className="flex-1" />
        <Button variant="outline" size="sm" onClick={() => setShowPaste(true)} data-testid="paste-intake-btn"><ClipboardPaste className="mr-1.5 h-4 w-4" /> Paste Intake</Button>
        <Button variant="outline" size="sm" onClick={() => setShowVendors(true)} data-testid="manage-vendors-btn"><Building2 className="mr-1.5 h-4 w-4" /> Vendors</Button>
      </div>

      {loading ? (
        <div className="grid place-items-center py-16 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /></div>
      ) : rows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border bg-card py-16 text-center" data-testid="queue-empty">
          <Inbox className="mx-auto h-8 w-8 text-muted-foreground/50" />
          <p className="mt-3 text-sm text-muted-foreground">No submissions with status “{status}”.</p>
        </div>
      ) : (
        <div className="grid gap-3 lg:grid-cols-2">
          {rows.map((s) => (
            <div key={s.id} className="rounded-lg border border-border bg-card p-4" data-testid={`sub-${s.display_id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-heading font-semibold text-sm truncate">{s.client_name || "Unnamed"}</span>
                    <span className="font-mono text-[11px] text-muted-foreground">{s.display_id}</span>
                  </div>
                  <div className="text-[12px] text-muted-foreground mt-0.5">{s.vendor_name} · {s.state || "—"} · {s.product_interest || "—"} · ${s.lead_cost || "0"}</div>
                </div>
                <span className={`shrink-0 inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ring-1 ring-inset ${STATUS_CLS[s.status]}`}>{s.status}</span>
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {s.dnc_flag && <Badge cls="bg-red-50 text-red-700 ring-red-200"><ShieldAlert className="h-3 w-3" /> On DNC / opt-out</Badge>}
                {s.duplicate_flag && <Badge cls="bg-amber-50 text-amber-700 ring-amber-200"><AlertTriangle className="h-3 w-3" /> Possible duplicate {s.duplicate_of}</Badge>}
                {s.has_sensitive && <Badge cls="bg-slate-100 text-slate-600 ring-slate-200">Sensitive data quarantined</Badge>}
              </div>
              {s.missing_info && <div className="mt-2 text-[12px] text-amber-700">Missing: {s.missing_info}</div>}
              {s.decision && <div className="mt-2 text-[11px] text-muted-foreground">{s.decision.action} by {s.decision.by} · {new Date(s.decision.at).toLocaleString()}</div>}

              {s.status !== "Accepted" && s.status !== "Rejected" && (
                <div className="mt-3 flex flex-wrap gap-1.5 border-t border-border pt-2.5">
                  <Button size="sm" className="h-7 text-xs" onClick={() => setAccepting(s)} data-testid={`accept-${s.display_id}`}><CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Accept</Button>
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => decide(s, "hold")}><PauseCircle className="mr-1 h-3.5 w-3.5" /> Hold</Button>
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setReturning(s)}><RotateCcw className="mr-1 h-3.5 w-3.5" /> Return</Button>
                  <Button size="sm" variant="outline" className="h-7 text-xs text-destructive" onClick={() => decide(s, "reject")} data-testid={`reject-${s.display_id}`}><XCircle className="mr-1 h-3.5 w-3.5" /> Reject</Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {accepting && <AcceptDialog sub={accepting} agents={opts.agents || []} onClose={() => setAccepting(null)} onAccept={(extra) => decide(accepting, "accept", extra)} />}
      {returning && <ReturnDialog sub={returning} onClose={() => setReturning(null)} onReturn={(missing_info) => decide(returning, "return", { missing_info })} />}
      {showPaste && <PasteDialog onClose={() => setShowPaste(false)} onDone={load} />}
      {showVendors && <VendorsDialog onClose={() => setShowVendors(false)} />}
    </div>
  );
}

function Badge({ children, cls }) {
  return <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ring-1 ring-inset ${cls}`}>{children}</span>;
}

function Modal({ title, icon: Icon, onClose, children, wide }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className={`relative w-full ${wide ? "max-w-2xl" : "max-w-md"} max-h-[88vh] overflow-auto rounded-xl bg-card p-5 shadow-2xl`}>
        <div className="flex items-center justify-between mb-4">
          <div className="font-heading text-lg font-bold flex items-center gap-2">{Icon && <Icon className="h-5 w-5 text-[#315A7D]" />} {title}</div>
          <button onClick={onClose}><X className="h-5 w-5 text-muted-foreground hover:text-foreground" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function AcceptDialog({ sub, agents, onClose, onAccept }) {
  const [agent, setAgent] = useState("");
  const [cost, setCost] = useState(sub.lead_cost || "");
  const [terms, setTerms] = useState("");
  return (
    <Modal title="Accept Lead into Pipeline" icon={CheckCircle2} onClose={onClose}>
      <p className="text-sm text-muted-foreground mb-4">Accepting <strong>{sub.client_name}</strong> ({sub.display_id}) creates a live <strong>New Accepted Lead</strong>, records vendor payment terms, sets the first action due today, and alerts the team.</p>
      <div className="space-y-3">
        <div><Label className="text-xs">Assign Licensed Agent</Label>
          <Select value={agent} onValueChange={setAgent}>
            <SelectTrigger data-testid="accept-agent"><SelectValue placeholder="Select agent" /></SelectTrigger>
            <SelectContent>{agents.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Lead Cost ($)</Label><Input value={cost} onChange={(e) => setCost(e.target.value)} /></div>
          <div><Label className="text-xs">Payment Terms</Label><Input value={terms} onChange={(e) => setTerms(e.target.value)} placeholder="e.g. Net 15" /></div>
        </div>
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={() => onAccept({ assigned_agent: agent, lead_cost: cost, payment_terms: terms })} disabled={!agent} data-testid="accept-confirm">Accept &amp; Add to Pipeline</Button>
      </div>
    </Modal>
  );
}

function ReturnDialog({ sub, onClose, onReturn }) {
  const [missing, setMissing] = useState("");
  return (
    <Modal title="Return for Missing Information" icon={RotateCcw} onClose={onClose}>
      <Label className="text-xs">What information is missing?</Label>
      <Textarea value={missing} onChange={(e) => setMissing(e.target.value)} placeholder="e.g. Date of birth, beneficiary, coverage amount" data-testid="return-missing" />
      <div className="mt-4 flex justify-end gap-2">
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={() => onReturn(missing)} disabled={!missing}>Return to Vendor</Button>
      </div>
    </Modal>
  );
}

function PasteDialog({ onClose, onDone }) {
  const [text, setText] = useState("");
  const [parsed, setParsed] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [vendorId, setVendorId] = useState("");
  useEffect(() => { api.get("/vendors").then(({ data }) => setVendors(data)).catch(() => {}); }, []);
  const parse = async () => {
    try { const { data } = await api.post("/intake/paste/parse", { text }); setParsed(data); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const submit = async () => {
    try {
      await api.post("/intake/submit", { vendor_id: vendorId, fields: parsed });
      toast.success("Submission added to review queue"); onDone(); onClose();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  return (
    <Modal title="Paste Intake" icon={ClipboardPaste} onClose={onClose} wide>
      <Label className="text-xs">Paste raw lead text (email, message, etc.)</Label>
      <Textarea rows={5} value={text} onChange={(e) => setText(e.target.value)} data-testid="paste-text" />
      <Button size="sm" className="mt-2" onClick={parse} disabled={!text}>Extract Fields</Button>
      {parsed && (
        <div className="mt-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            {["client_name", "phone", "email", "state"].map((k) => (
              <div key={k}><Label className="text-xs capitalize">{k.replace("_", " ")}</Label>
                <Input value={parsed[k] || ""} onChange={(e) => setParsed({ ...parsed, [k]: e.target.value })} /></div>
            ))}
          </div>
          <div><Label className="text-xs">Vendor</Label>
            <Select value={vendorId} onValueChange={setVendorId}>
              <SelectTrigger><SelectValue placeholder="Attribute to vendor" /></SelectTrigger>
              <SelectContent>{vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex justify-end"><Button onClick={submit} disabled={!vendorId} data-testid="paste-submit">Add to Review Queue</Button></div>
        </div>
      )}
    </Modal>
  );
}

function VendorsDialog({ onClose }) {
  const [vendors, setVendors] = useState([]);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [created, setCreated] = useState(null);
  const load = () => api.get("/vendors").then(({ data }) => setVendors(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const create = async () => {
    try {
      const { data } = await api.post("/vendors", { name, contact_email: email });
      setCreated(data); setName(""); setEmail(""); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  return (
    <Modal title="Lead Vendors" icon={Building2} onClose={onClose} wide>
      <div className="flex items-end gap-2 mb-4">
        <div className="flex-1"><Label className="text-xs">Vendor name</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="vendor-name" /></div>
        <div className="flex-1"><Label className="text-xs">Login email (optional)</Label><Input value={email} onChange={(e) => setEmail(e.target.value)} /></div>
        <Button onClick={create} disabled={!name} data-testid="vendor-create"><Plus className="mr-1 h-4 w-4" /> Add</Button>
      </div>
      {created && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-[12px]" data-testid="vendor-created">
          <div className="font-semibold text-amber-900 flex items-center gap-1"><KeyRound className="h-3.5 w-3.5" /> Save these now — shown once</div>
          <div className="mt-1 font-mono break-all">Webhook token: {created.webhook_token}
            <button className="ml-2 text-[#315A7D]" onClick={() => { navigator.clipboard?.writeText(created.webhook_token); toast.success("Copied"); }}><Copy className="inline h-3 w-3" /></button></div>
          {created.temp_password && <div className="mt-1">Login: {created.login_email} · temp password: <span className="font-mono">{created.temp_password}</span></div>}
          <div className="mt-1 text-amber-700">POST leads to <span className="font-mono">/api/intake/webhook</span> with header <span className="font-mono">X-Vendor-Token</span>.</div>
        </div>
      )}
      <div className="space-y-1.5">
        {vendors.map((v) => (
          <div key={v.id} className="flex items-center justify-between rounded-md border border-border px-3 py-2 text-sm">
            <span className="font-medium">{v.name}</span>
            <span className="text-xs text-muted-foreground">{v.contact_email || "no login"}</span>
          </div>
        ))}
      </div>
    </Modal>
  );
}
